import { useEffect, useMemo, useState } from "react";
import { Activity, RefreshCw, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatDateTime(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function titleCase(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function actorName(event) {
  return event.user_name || event.user_email || (event.user_id ? `User ${event.user_id}` : "System");
}

const ACTION_LABELS = {
  delete: "Deleted",
  get: "Viewed",
  login: "Logged in",
  patch: "Updated",
  post: "Created",
  put: "Updated",
  reverse: "Reversed",
  share: "Shared",
  update: "Updated",
};

const API_ACTION_LABELS = [
  [/^POST \/api\/auth\/change-password$/, "Changed password", "Updated"],
  [/^POST \/api\/auth\/logout$/, "Logged out", "Logged out"],
  [/^POST \/api\/auth\/forgot-password$/, "Requested password reset", "Requested"],
  [/^POST \/api\/auth\/reset-password$/, "Reset password", "Updated"],
  [/^POST \/api\/auth\/refresh$/, "Refreshed session", "Updated"],
  [/^POST \/api\/clients$/, "Created client", "Created"],
  [/^PUT \/api\/clients\/:client_id$/, "Updated client", "Updated"],
  [/^DELETE \/api\/clients\/:client_id$/, "Deleted client", "Deleted"],
  [/^POST \/api\/hoardings$/, "Created hoarding", "Created"],
  [/^PUT \/api\/hoardings\/:hoarding_pk$/, "Updated hoarding", "Updated"],
  [/^DELETE \/api\/hoardings\/:hoarding_pk$/, "Deleted hoarding", "Deleted"],
  [/^POST \/api\/campaigns$/, "Created campaign", "Created"],
  [/^PUT \/api\/campaigns\/:campaign_pk$/, "Updated campaign", "Updated"],
  [/^DELETE \/api\/campaigns\/:campaign_pk$/, "Deleted campaign", "Deleted"],
  [/^POST \/api\/campaigns\/:campaign_pk\/proposal$/, "Created campaign quotation", "Created"],
  [/^POST \/api\/campaigns\/:campaign_pk\/proofs$/, "Uploaded campaign proof", "Uploaded"],
  [/^POST \/api\/campaigns\/:campaign_pk\/duplicate$/, "Duplicated campaign", "Created"],
  [/^POST \/api\/campaigns\/:campaign_pk\/renew$/, "Renewed campaign", "Updated"],
  [/^POST \/api\/proposals$/, "Saved proposal", "Created"],
  [/^POST \/api\/proposals\/generate\/pdf$/, "Generated proposal PDF", "Generated"],
  [/^POST \/api\/proposals\/generate\/excel$/, "Generated proposal Excel", "Generated"],
  [/^POST \/api\/proposals\/generate\/ppt$/, "Generated proposal PPT", "Generated"],
  [/^POST \/api\/proposals\/:proposal_id\/share-link$/, "Shared proposal link", "Shared"],
  [/^POST \/api\/accounts\/.+/, "Updated accounts", "Updated"],
  [/^POST \/api\/finance-ops\/.+/, "Updated finance work queue", "Updated"],
];

function apiActionDisplay(event) {
  const method = String(event.request_body?.method || event.action || "").toUpperCase();
  const path = String(event.request_body?.path || "");
  const key = `${method} ${path}`;
  const match = API_ACTION_LABELS.find(([pattern]) => pattern.test(key));
  if (match) return { summary: match[1], action: match[2] };
  const fallbackAction = ACTION_LABELS[String(event.action || "").toLowerCase()] || titleCase(event.action || "Action");
  const entity = titleCase(String(event.entity_type || "").replace(/^api_?/, "")) || "Platform";
  return { summary: `${fallbackAction} ${entity}`.trim(), action: fallbackAction };
}

function eventSummary(event) {
  if (event.event_name === "api_action") {
    return apiActionDisplay(event).summary;
  }
  return titleCase(event.event_name || event.action);
}

function actionLabel(event) {
  if (event.event_name === "api_action") return apiActionDisplay(event).action;
  return ACTION_LABELS[String(event.action || "").toLowerCase()] || titleCase(event.action);
}

function eventDetail(event) {
  const parts = [
    event.event_name !== "api_action" && event.entity_type && titleCase(event.entity_type),
    event.entity_id && `#${event.entity_id}`,
    event.branch_type && event.branch_type !== "main" && titleCase(event.branch_type),
  ].filter(Boolean);
  return parts.join(" | ");
}

function eventTone(action) {
  const value = String(action || "").toLowerCase();
  if (value.includes("delete") || value.includes("reverse")) return "border-rose-200 bg-rose-50 text-rose-700";
  if (value.includes("create") || value.includes("post") || value.includes("share")) return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (value.includes("update") || value.includes("put") || value.includes("patch")) return "border-blue-200 bg-blue-50 text-blue-700";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

export function AuditLogs() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [entityType, setEntityType] = useState("all");
  const [query, setQuery] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (entityType !== "all") params.entity_type = entityType;
      const { data } = await api.get("/audit-events", { params, skipGlobalLoader: true });
      setEvents(asArray(data));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Audit logs could not be loaded");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType]);

  const entityTypes = useMemo(() => {
    const values = [...new Set(events.map((event) => event.entity_type).filter(Boolean))].sort();
    return values.length > 0 ? values : ["campaign", "client", "hoarding", "proposal", "invoice", "payment"];
  }, [events]);

  const visibleEvents = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return events;
    return events.filter((event) => [
      event.event_name,
      event.action,
      event.entity_type,
      event.entity_id,
      event.user_name,
      event.user_email,
      event.request_body?.path,
    ].filter(Boolean).some((value) => String(value).toLowerCase().includes(needle)));
  }, [events, query]);

  return (
    <Layout
      title="Audit Logs"
      subtitle="Last 15 days of platform actions, newest first"
      actions={(
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw size={15} /> Refresh
        </Button>
      )}
      mainClassName="bg-[#F8FAFC] p-6"
    >
      <div className="mx-auto w-full max-w-[1400px] space-y-4">
        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                <ShieldCheck size={20} />
              </span>
              <div>
                <div className="text-sm font-black text-slate-950">Admin visibility</div>
                <div className="text-xs font-semibold text-slate-500">Older logs are automatically deleted after 15 days.</div>
              </div>
            </div>
            <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-[220px_minmax(0,1fr)] lg:ml-auto lg:max-w-2xl">
              <Select value={entityType} onValueChange={setEntityType}>
                <SelectTrigger className="h-9 border-slate-300 bg-white">
                  <SelectValue placeholder="Entity type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All activity</SelectItem>
                  {entityTypes.map((type) => (
                    <SelectItem key={type} value={type}>{titleCase(type)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative">
                <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search action, user, entity..."
                  className="h-9 border-slate-300 bg-white pl-9"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div className="text-sm font-black text-slate-950">Activity</div>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
              {visibleEvents.length} logs
            </span>
          </div>
          <div className="divide-y divide-slate-100">
            {loading && (
              <div className="px-4 py-10 text-center text-sm font-semibold text-slate-500">Loading audit logs...</div>
            )}
            {!loading && visibleEvents.length === 0 && (
              <div className="px-4 py-12 text-center">
                <Activity className="mx-auto text-slate-300" size={30} />
                <div className="mt-2 text-sm font-bold text-slate-700">No audit logs found</div>
                <div className="text-xs text-slate-500">Only actions from the last 15 days are retained.</div>
              </div>
            )}
            {!loading && visibleEvents.map((event) => (
              <article key={event.id} className="grid gap-3 px-4 py-3 hover:bg-slate-50 md:grid-cols-[minmax(0,1fr)_180px] md:items-center">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-black ${eventTone(event.action)}`}>
                      {actionLabel(event)}
                    </span>
                    <h3 className="truncate text-sm font-black text-slate-950">{eventSummary(event)}</h3>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs font-semibold text-slate-500">
                    <span>{actorName(event)}</span>
                    {eventDetail(event) && <span>{eventDetail(event)}</span>}
                  </div>
                </div>
                <div className="text-left text-xs font-semibold text-slate-500 md:text-right">
                  {formatDateTime(event.created_at)}
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}
